// src/stores/DocumentStore.js - Zustand Store for Customer Documents
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useDocumentStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Documents indexed by customer ID
    // Structure: { customerId: { documents: [], count: 0, loading: false } }
    documentsByCustomer: {},

    // Upload state
    uploading: false,
    uploadProgress: 0,

    // Connection state
    isConnected: false,

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS
    // ================================

    /**
     * Load documents for a customer
     * @param {string} customerId - Customer ID
     * @param {object} options - { includeTags }
     */
    loadCustomerDocuments: (customerId, options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('DocumentStore: Socket service not initialized');
        return;
      }

      if (!customerId) {
        console.error('DocumentStore: Customer ID is required');
        return;
      }

      const { includeTags = true } = options;

      console.log('DocumentStore: Loading documents for customer:', customerId);

      // Set loading state for this customer
      set(state => ({
        documentsByCustomer: {
          ...state.documentsByCustomer,
          [customerId]: {
            ...state.documentsByCustomer[customerId],
            loading: true
          }
        }
      }));

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_CUSTOMER_DOCUMENTS, {
        customer_id: customerId,
        include_tags: includeTags
      });
    },

    /**
     * Add a document to a customer (after file is uploaded to storage)
     * @param {object} documentData - Document metadata
     */
    addDocument: (documentData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('DocumentStore: Socket service not initialized');
        return;
      }

      if (!documentData.customer_id || !documentData.file_name || !documentData.file_url) {
        console.error('DocumentStore: Customer ID, file name, and file URL are required');
        return;
      }

      console.log('DocumentStore: Adding document for customer:', documentData.customer_id);

      socketManager.universalHandler.emit(SOCKET_EVENTS.ADD_CUSTOMER_DOCUMENT, documentData);
    },

    /**
     * Update document metadata
     * @param {string} documentId - Document ID
     * @param {object} updates - Fields to update
     */
    updateDocument: (documentId, updates) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('DocumentStore: Socket service not initialized');
        return;
      }

      if (!documentId) {
        console.error('DocumentStore: Document ID is required');
        return;
      }

      console.log('DocumentStore: Updating document:', documentId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_CUSTOMER_DOCUMENT, {
        document_id: documentId,
        ...updates
      });
    },

    /**
     * Delete a document
     * @param {string} documentId - Document ID
     */
    deleteDocument: (documentId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('DocumentStore: Socket service not initialized');
        return;
      }

      if (!documentId) {
        console.error('DocumentStore: Document ID is required');
        return;
      }

      console.log('DocumentStore: Deleting document:', documentId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_CUSTOMER_DOCUMENT, {
        document_id: documentId
      });
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get documents for a specific customer
     */
    getDocumentsForCustomer: (customerId) => {
      const { documentsByCustomer } = get();
      return documentsByCustomer[customerId]?.documents || [];
    },

    /**
     * Get loading state for a customer
     */
    isLoadingDocumentsForCustomer: (customerId) => {
      const { documentsByCustomer } = get();
      return documentsByCustomer[customerId]?.loading || false;
    },

    /**
     * Get total count for a customer
     */
    getTotalDocumentsForCustomer: (customerId) => {
      const { documentsByCustomer } = get();
      return documentsByCustomer[customerId]?.count || 0;
    },

    /**
     * Set upload state
     */
    setUploading: (uploading, progress = 0) => {
      set({ uploading, uploadProgress: progress });
    },

    /**
     * Clear documents for a customer (e.g., when navigating away)
     */
    clearDocumentsForCustomer: (customerId) => {
      set(state => {
        const updated = { ...state.documentsByCustomer };
        delete updated[customerId];
        return { documentsByCustomer: updated };
      });
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setDocumentsLoaded: (customerId, data) => {
      console.log('DocumentStore: Documents loaded for customer:', customerId);

      set(state => ({
        documentsByCustomer: {
          ...state.documentsByCustomer,
          [customerId]: {
            documents: data.documents || [],
            count: data.count || 0,
            loading: false
          }
        }
      }));
    },

    _addDocument: (data) => {
      const customerId = data.customer_id;
      const document = data.document || data;
      if (!customerId) return;

      set(state => {
        const customerDocs = state.documentsByCustomer[customerId] || { documents: [], count: 0, loading: false };
        return {
          documentsByCustomer: {
            ...state.documentsByCustomer,
            [customerId]: {
              ...customerDocs,
              documents: [document, ...customerDocs.documents],
              count: customerDocs.count + 1
            }
          },
          uploading: false,
          uploadProgress: 0
        };
      });
    },

    _updateDocument: (data) => {
      const customerId = data.customer_id;
      const document = data.document || data;
      if (!customerId) return;

      set(state => {
        const customerDocs = state.documentsByCustomer[customerId];
        if (!customerDocs) return state;

        return {
          documentsByCustomer: {
            ...state.documentsByCustomer,
            [customerId]: {
              ...customerDocs,
              documents: customerDocs.documents.map(doc =>
                doc.id === document.id ? document : doc
              )
            }
          }
        };
      });
    },

    _removeDocument: (data) => {
      // Backend sends 'id', not 'document_id'
      const documentId = data.document_id || data.id;
      const { customer_id } = data;

      set(state => {
        const updatedDocumentsByCustomer = { ...state.documentsByCustomer };

        // If customer_id provided, use it directly
        if (customer_id && state.documentsByCustomer[customer_id]) {
          const customerDocs = state.documentsByCustomer[customer_id];
          updatedDocumentsByCustomer[customer_id] = {
            ...customerDocs,
            documents: customerDocs.documents.filter(doc => doc.id !== documentId),
            count: Math.max(0, customerDocs.count - 1)
          };
        } else {
          // Search through all customers to find the document
          for (const [custId, customerData] of Object.entries(state.documentsByCustomer)) {
            const docIndex = customerData.documents.findIndex(d => d.id === documentId);
            if (docIndex !== -1) {
              updatedDocumentsByCustomer[custId] = {
                ...customerData,
                documents: customerData.documents.filter(doc => doc.id !== documentId),
                count: Math.max(0, customerData.count - 1)
              };
              break;
            }
          }
        }

        return { documentsByCustomer: updatedDocumentsByCustomer };
      });
    },

    _setDocumentsLoadFailed: (customerId) => {
      if (!customerId) return;

      set(state => ({
        documentsByCustomer: {
          ...state.documentsByCustomer,
          [customerId]: {
            ...state.documentsByCustomer[customerId],
            loading: false
          }
        }
      }));
    },

    /**
     * Add a tag to a document (called by tag event listener)
     */
    _addTagToDocument: (documentId, tagging) => {
      set(state => {
        const updatedDocumentsByCustomer = { ...state.documentsByCustomer };

        for (const [customerId, customerData] of Object.entries(state.documentsByCustomer)) {
          const docIndex = customerData.documents.findIndex(d => d.id === documentId);
          if (docIndex !== -1) {
            const doc = customerData.documents[docIndex];
            const existingTags = doc.tags || [];

            // Check if tag already exists
            if (!existingTags.some(t => t.tag?.id === tagging.tag?.id || t.tag_id === tagging.tag_id)) {
              const updatedDoc = {
                ...doc,
                tags: [...existingTags, tagging]
              };

              updatedDocumentsByCustomer[customerId] = {
                ...customerData,
                documents: customerData.documents.map((d, i) => i === docIndex ? updatedDoc : d)
              };
            }
            break;
          }
        }

        return { documentsByCustomer: updatedDocumentsByCustomer };
      });
    },

    /**
     * Remove a tag from a document (called by tag event listener)
     */
    _removeTagFromDocument: (documentId, tagId) => {
      set(state => {
        const updatedDocumentsByCustomer = { ...state.documentsByCustomer };

        for (const [customerId, customerData] of Object.entries(state.documentsByCustomer)) {
          const docIndex = customerData.documents.findIndex(d => d.id === documentId);
          if (docIndex !== -1) {
            const doc = customerData.documents[docIndex];
            const updatedDoc = {
              ...doc,
              tags: (doc.tags || []).filter(t =>
                t.tag?.id !== tagId && t.tag_id !== tagId && t.id !== tagId
              )
            };

            updatedDocumentsByCustomer[customerId] = {
              ...customerData,
              documents: customerData.documents.map((d, i) => i === docIndex ? updatedDoc : d)
            };
            break;
          }
        }

        return { documentsByCustomer: updatedDocumentsByCustomer };
      });
    },

    // ================================
    // INTERNAL METHODS
    // ================================

    _getSocketManager: () => {
      return {
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null
      };
    },

    _initialize: (socketManager) => {
      console.log('DocumentStore: Initializing with socket manager');

      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        isConnected: true,
        _initialized: true
      });
    },

    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },

    _handleAuthReset: () => {
      console.log('DocumentStore: Resetting all state due to auth reset');

      set({
        documentsByCustomer: {},
        uploading: false,
        uploadProgress: 0,
        isConnected: false,
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP
// ================================

// Track pending requests to map responses to customers
let pendingDocumentRequests = new Map();

export const initializeDocumentStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }

  console.log('DocumentStore: Setting up socket event listeners');

  const { universalHandler } = socketManager;

  // Intercept loadCustomerDocuments to track pending requests
  const originalLoadCustomerDocuments = useDocumentStore.getState().loadCustomerDocuments;
  useDocumentStore.setState({
    loadCustomerDocuments: (customerId, options = {}) => {
      pendingDocumentRequests.set('current', customerId);
      originalLoadCustomerDocuments(customerId, options);
    }
  });

  // Set up event listeners
  const documentEvents = [
    SOCKET_EVENTS.CUSTOMER_DOCUMENTS_LOADED,
    SOCKET_EVENTS.CUSTOMER_DOCUMENT_ADDED,
    SOCKET_EVENTS.CUSTOMER_DOCUMENT_UPDATED,
    SOCKET_EVENTS.CUSTOMER_DOCUMENT_DELETED,
    SOCKET_EVENTS.GET_CUSTOMER_DOCUMENTS_FAILED,
    SOCKET_EVENTS.ADD_CUSTOMER_DOCUMENT_FAILED,
    SOCKET_EVENTS.UPDATE_CUSTOMER_DOCUMENT_FAILED,
    SOCKET_EVENTS.DELETE_CUSTOMER_DOCUMENT_FAILED,
    // Tag events - DocumentStore handles tag updates for documents
    SOCKET_EVENTS.TAG_ADDED_TO_ENTITY,
    SOCKET_EVENTS.TAG_REMOVED_FROM_ENTITY
  ];

  documentEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      const store = useDocumentStore.getState();

      switch (eventName) {
        case SOCKET_EVENTS.CUSTOMER_DOCUMENTS_LOADED:
          const customerId = pendingDocumentRequests.get('current');
          if (customerId) {
            store._setDocumentsLoaded(customerId, data);
          }
          pendingDocumentRequests.delete('current');
          break;

        case SOCKET_EVENTS.CUSTOMER_DOCUMENT_ADDED:
          store._addDocument(data);
          break;

        case SOCKET_EVENTS.CUSTOMER_DOCUMENT_UPDATED:
          store._updateDocument(data);
          break;

        case SOCKET_EVENTS.CUSTOMER_DOCUMENT_DELETED:
          store._removeDocument(data);
          break;

        case SOCKET_EVENTS.GET_CUSTOMER_DOCUMENTS_FAILED:
          const failedCustomerId = pendingDocumentRequests.get('current');
          store._setDocumentsLoadFailed(failedCustomerId);
          pendingDocumentRequests.delete('current');
          console.error('DocumentStore: Failed to load documents:', data?.message);
          break;

        case SOCKET_EVENTS.ADD_CUSTOMER_DOCUMENT_FAILED:
          store.setUploading(false, 0);
          console.error('DocumentStore: Failed to add document:', data?.message);
          break;

        case SOCKET_EVENTS.UPDATE_CUSTOMER_DOCUMENT_FAILED:
        case SOCKET_EVENTS.DELETE_CUSTOMER_DOCUMENT_FAILED:
          console.error('DocumentStore: Operation failed:', data?.message);
          break;

        case SOCKET_EVENTS.TAG_ADDED_TO_ENTITY:
          // Only handle document-related tag events
          if (data.taggable_type === 'customer_document' || data.taggable_type === 'document') {
            console.log('DocumentStore: Tag added to document:', data.taggable_id);
            store._addTagToDocument(data.taggable_id, data.tagging);
          }
          break;

        case SOCKET_EVENTS.TAG_REMOVED_FROM_ENTITY:
          // Only handle document-related tag events
          if (data.taggable_type === 'customer_document' || data.taggable_type === 'document') {
            console.log('DocumentStore: Tag removed from document:', data.taggable_id);
            store._removeTagFromDocument(data.taggable_id, data.tag_id);
          }
          break;
      }
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useDocumentStore.getState()._handleAuthReset();
      pendingDocumentRequests.clear();
    });
  }

  // Initialize the store
  useDocumentStore.getState()._initialize(socketManager);

  console.log('DocumentStore: Socket event listeners initialized');
};

export default useDocumentStore;
